import {useMutation, useQuery} from '@apollo/client'
import {FormEvent, useEffect, useState} from 'react'
import {Button, Form, Modal, Spinner} from 'react-bootstrap'
import {toast} from 'react-toastify'
import {EDIT_POLICY} from '../../../gql/mutations/policyMutation'
import {
  AllPolicyStatusesData,
  AllPolicyStatusesVars,
  IResUpdatePolicyApi,
} from '../../../types/policies'
import {parseId} from '../../../helpers'
import Select from 'react-select'
import {GET_ALL_POLICIES_STATUSES} from '../../../gql/queries/policiesQueries'
import {GET_ONE_POLICY_API} from '../../../gql/queries/policiesApiQueries'

import {getBootstrapColorHex, ALL_PERMISSIONS} from '../../../helpers/consts'
import {NodePolicy} from '../../../types/policiesApi'
import { useGetUser } from '../../../store/userStore'
import { hasPermission } from '../../../helpers'

interface IModalProps {
  node: NodePolicy | undefined
  isOpen: boolean
  onClose: () => void
  refetch: () => void
}

export const ModalQuickUpdates: React.FC<IModalProps> = ({node, isOpen, onClose, refetch}) => {
  // Obtener el usuario actual, sus grupos y permisos
  const userGroups = useGetUser((s) => s.user?.groups) || []
  const permissions = useGetUser((s) => s.user?.permissions)
  const isAsesor = userGroups.includes('Asesor')
  const canMarkAsCompleted = permissions && hasPermission(permissions, ALL_PERMISSIONS.MARK_COMPLETED_POLICY)

  // Si tiene el permiso MARK_COMPLETED_POLICY, puede editar todo sin restricciones
  const hasRestrictedAccess = isAsesor && !canMarkAsCompleted
  const isQuotePolicy = node?.type === 'QUOTE'

  // Status codes permitidos para Asesores sin permiso especial
  const allowedStatusCodesForAsesor = ['NEW', 'RENEWED', 'PROCESADO']

  // Función para verificar si un status code es permitido para Asesor
  const isStatusAllowedForAsesor = (code: string): boolean => {
    // Verificar si es uno de los códigos exactos permitidos
    if (allowedStatusCodesForAsesor.includes(code)) {
      return true
    }
    // Verificar si comienza con UPDATED_BY_CLIENT
    if (code.startsWith('UPDATED_BY_CLIENT')) {
      return true
    }
    return false
  }

  const {
    data: dataPoliciesStatuses,
    loading: loadingPoliciesStatus,
    error: errorPoliciesStatuses,
  } = useQuery<AllPolicyStatusesData, AllPolicyStatusesVars>(GET_ALL_POLICIES_STATUSES, {
    fetchPolicy: 'cache-first',
  })

  // Filtrar los status según el rol del usuario
  const allStatusOptions =
    dataPoliciesStatuses?.allPolicyStatuses.edges.map(({node}) => ({
      value: node.code,
      label: node.title,
      color: getBootstrapColorHex(node.color || 'gray'),
      icon: (
        <i
          className='bi bi-circle-fill'
          style={{color: getBootstrapColorHex(node.color || 'gray')}}
        ></i>
      ),
    })) || []

  // Si es Asesor sin permiso especial, filtrar solo los status permitidos
  const statusOptions = hasRestrictedAccess
    ? allStatusOptions.filter((option) => isStatusAllowedForAsesor(option.value))
    : allStatusOptions

  const customSingleValue = ({data}: any) => (
    <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
      {data.icon}
      {data.label}
    </div>
  )

  const customOption = (props: any) => {
    const {data, innerRef, innerProps} = props
    return (
      <div
        ref={innerRef}
        {...innerProps}
        style={{display: 'flex', alignItems: 'center', padding: 10}}
      >
        {data.icon}
        <span style={{marginLeft: 8}}>{data.label}</span>
      </div>
    )
  }

  const [formData, setFormData] = useState({
    statusPolicy: '',
    documentStatus: '',
    paymentStatus: '',
    memberId: '',
    effectiveDate: '',
    commissionPayable: false,
  })

  const [gqlCreateP, {data: dataUpdate, loading: loadingUpdate, error: errorUpdate}] =
    useMutation<IResUpdatePolicyApi>(EDIT_POLICY)

  useEffect(() => {
    if (!dataUpdate) return

    if (dataUpdate && dataUpdate.updatePolicy.success) {
      toast.success('Status updated successfully')
      refetch()
      onClose()
    } else if (dataUpdate.updatePolicy.errors && dataUpdate.updatePolicy.errors.length > 0) {
      toast.error(`Error ${dataUpdate.updatePolicy.errors[0].message}`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUpdate])

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!node) {
      toast.error('No policy data available to update')
      return
    }
    const fieldsToUpdate = [
      ...(!isQuotePolicy ? [{key: 'status', value: formData.statusPolicy}] : []),
      {key: 'stateDocs', value: formData.documentStatus},
      {key: 'paymentStatus', value: formData.paymentStatus},
      {key: 'memberId', value: formData.memberId},
      {key: 'effectiveDate', value: formData.effectiveDate},
      {key: 'commissionPayable', value: formData.commissionPayable},
    ]

    const policyData = fieldsToUpdate.reduce((acc, {key, value}) => {
      if (value !== null && value !== undefined && value !== '') {
        acc[key] = value
      }
      return acc
    }, {} as Record<string, any>)

    gqlCreateP({
      variables: {
        id: parseId(node.id),
        policyData,
      },
    })
  }

  // const {data: policyData, loading: loadingPolicy} = useQuery(GET_ONE_POLICY_API, {
  //   variables: {id: parseId(id)},
  //   skip: !isOpen, // evita llamar la query si el modal no está abierto
  // })

  // console.log('🚀 policyData v1:', policyData)

  useEffect(() => {
    if (isOpen && node) {
      console.log('✅ Precargando formData con:', node)
      const p = node
      setFormData({
        statusPolicy: p.status || '',
        documentStatus: p.stateDocs || '',
        paymentStatus: p.paymentStatus || '',
        memberId: p.memberId || '',
        effectiveDate: '' + p.effectiveDate || '',
        commissionPayable: p.commissionPayable ?? false,
      })
    }

    if (!isOpen) {
      console.log('🔄 Modal cerrado, limpiando formData')
      // Limpiar al cerrar
      setFormData({
        statusPolicy: '',
        documentStatus: '',
        paymentStatus: '',
        memberId: '',
        effectiveDate: '',
        commissionPayable: false,
      })
    }
  }, [node, isOpen])

  useEffect(() => {
    console.log('📦 formData actual:', formData)
  }, [formData])

  return (
    <Modal show={isOpen} onHide={onClose} className='modal-statuss-policy'>
      <Modal.Header closeButton>
        <Modal.Title>Quick Policy Update</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form id='formId' onSubmit={handleSubmit}>
          {!isQuotePolicy && (
            <Form.Group>
              <Form.Label>Status</Form.Label>
              <Select
                className='selectable'
                options={statusOptions}
                value={statusOptions.find((opt) => opt.value === formData.statusPolicy)}
                onChange={(selected) =>
                  setFormData((prev) => ({
                    ...prev,
                    statusPolicy: selected?.value || '',
                  }))
                }
                isSearchable={true}
                components={{SingleValue: customSingleValue, Option: customOption}}
                styles={{
                  control: (base) => ({...base, minHeight: 40}),
                  option: (base, {data, isFocused}) => ({
                    ...base,
                    backgroundColor: isFocused ? '#f0f0f0' : 'white',
                    color: data.color,
                  }),
                }}
                placeholder='Type or select a status'
                isDisabled={loadingPoliciesStatus}
              />
            </Form.Group>
          )}
          <Form.Group className='mt-2'>
            <Form.Label>Documents status</Form.Label>
            <Form.Select
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  documentStatus: e.target.value,
                }))
              }
              value={formData.documentStatus ?? ''}
              disabled={hasRestrictedAccess}
            >
              <option value=''>Select one</option>
              <option value='ACTIVE'>Active</option>
              <option value='PENDING'>Pending</option>
              <option value='PROCESSING'>Processing</option>
              <option value='DECLINED'>Declined</option>
              <option value='COMPLETED'>Completed</option>
            </Form.Select>
            {hasRestrictedAccess && (
              <Form.Text className='text-muted'>
                You don't have permissions to modify document status
              </Form.Text>
            )}
          </Form.Group>
          <Form.Group className='mt-2'>
            <Form.Label>Payment status</Form.Label>
            <Form.Select
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  paymentStatus: e.target.value,
                }))
              }
              value={formData.paymentStatus ?? ''}
              disabled={hasRestrictedAccess}
            >
              <option value=''>Select one</option>
              <option value='PAID'>Paid</option>
              <option value='FAILED'>Failed</option>
              <option value='PENDING'>Pending</option>
              <option value='AUTO_PAY'>Auto Pay</option>
              <option value='NOT_APPLICABLE'>Not Applicable ($0)</option>
              <option value='PAGA_POR_SU_CUENTA'>Paga por su cuenta</option>
              <option value='CHARGEBACK'>Chargeback</option>
            </Form.Select>
            {hasRestrictedAccess && (
              <Form.Text className='text-muted'>
                You don't have permissions to modify payment status
              </Form.Text>
            )}
          </Form.Group>
          <Form.Group className='mt-2'>
            <Form.Label>Member ID</Form.Label>
            <Form.Control
              value={formData.memberId || ''}
              name='memberId'
              id='memberId'
              placeholder='Insert Member ID'
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  memberId: e.target.value,
                }))
              }
            />
          </Form.Group>
          <Form.Group className='mt-2'>
            <Form.Label>Effective date</Form.Label>
            <Form.Control
              type='date'
              value={formData.effectiveDate}
              onChange={(e) => setFormData({...formData, effectiveDate: e.target.value})}
            />
          </Form.Group>
          <h6 className='mt-3'>Commissions Parameters</h6>
          <div className='commission-switch-wrapper'>
            <Form.Check
              label={`Consider this policy for commission reports? ${formData.commissionPayable ? '(YES)' : '(NO)'}`}
              type='switch'
              name='commissionPayable'
              id='commissionPayable'
              checked={formData.commissionPayable}
              onChange={(e) => setFormData({...formData, commissionPayable: e.target.checked})}
            />
          </div>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' size='sm' onClick={onClose}>
          Cancel
        </Button>
        <Button
          size='sm'
          variant='primary'
          type='submit'
          form='formId'
          formAction='formId'
          disabled={loadingUpdate}
        >
          {loadingUpdate && <Spinner animation='border' size='sm' className='me-2' />}
          Save Changes
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
